import React, { useCallback, useEffect } from 'react';

import { getBooleanField, getTextField } from '@lender/utils';
import { Box, Typography } from '@mui/material';
import { CheckboxCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';
import { object } from 'yup';

import useForm from 'hooks/useForm';
import { getActionProperty, isPdfFormType } from 'utils/forms';

import { formsTestIds } from '../../forms.testIds';

import DynamicFieldBoldCheckbox from './DynamicFieldBoldCheckbox';
import { fullWidthCellStyles, sectionHeaderStyles } from './DynamicFieldOptions.styles';
import { getInitialHorizontalAlignment } from './DynamicFieldOptions.utils';
import DynamicFieldOptionsLayout from './DynamicFieldOptionsLayout';
import StyleViewOptions from './StyleViewOptions';

const schema = object().shape({
  excludeSignatureLine: getBooleanField({ isRequired: false }),
  excludeDate: getBooleanField({ isRequired: false }),
  isBold: getBooleanField({ isRequired: false }),
  isUppercase: getBooleanField({ isRequired: false }),
  fontSize: getTextField({ isRequired: false }),
  horizontalAlignment: getTextField({ isRequired: false }),
  fontColor: getTextField({ isRequired: false }),
  fontFamily: getTextField({ isRequired: false }),
});

const DynamicFieldHandWritingSignatureOptions = ({
  setParentView,
  chosenOption,
  handleClose,
  editor,
  onSave,
  action,
  style,
  isNewItem = false,
}) => {
  const [title, value, type, label] = chosenOption;
  const isPdfForm = isPdfFormType(editor.formType);

  const [excludeSignatureLine, excludeDate] = action?.args || [false, false];

  const { form, setSchema, setInForm, validateField, handleChange } = useForm({
    excludeSignatureLine,
    excludeDate,
    isBold: !!style?.isBold,
    isUppercase: !!style?.isUppercase,
    horizontalAlignment: isPdfForm
      ? getInitialHorizontalAlignment({ align: style?.align, isNewItem })
      : undefined,
    fontColor: style?.fontColor || '',
    fontSize: { value: style?.fontSize },
    fontFamily: { value: style?.fontFamily },
  });

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      const action = getActionProperty({
        ...form,
        dataDynamicType: type,
      });

      onSave({
        title,
        value,
        type,
        label,
        action,
        style: {
          isBold: form.isBold,
          isUppercase: form.isUppercase,
          align: form.horizontalAlignment?.value,
          fontSize: form.fontSize?.value,
          fontColor: form.fontColor,
          fontFamily: form.fontFamily?.value,
        },
      });
    },
    [onSave, title, value, type, label, form],
  );

  return (
    <DynamicFieldOptionsLayout
      chosenOption={chosenOption}
      dataTestId={formsTestIds.DYNAMIC_FIELD_HAND_WRITING_SIGNATURE_OPTIONS}
      editor={editor}
      setParentView={setParentView}
      onClose={handleClose}
      onSubmit={onSubmit}
    >
      <Box sx={fullWidthCellStyles}>
        <Typography color="text.secondary" sx={sectionHeaderStyles} variant="bold12">
          Display Options & Formatting
        </Typography>
      </Box>

      <CheckboxCDS
        fullWidth
        dataTestId={formsTestIds.HAND_WRITING_EXCLUDE_SIGNATURE_LINE_CHECKBOX}
        label="Exclude Signature Line"
        name="excludeSignatureLine"
        value={form.excludeSignatureLine}
        onChange={handleChange}
      />

      <CheckboxCDS
        fullWidth
        dataTestId={formsTestIds.HAND_WRITING_EXCLUDE_DATE_CHECKBOX}
        label="Exclude Date"
        name="excludeDate"
        value={form.excludeDate}
        onChange={handleChange}
      />

      <DynamicFieldBoldCheckbox value={form.isBold} onChange={handleChange} />

      <Box />

      <StyleViewOptions
        fontColor={form.fontColor}
        fontFamily={form.fontFamily}
        fontSize={form.fontSize}
        handleChange={handleChange}
        horizontalAlignment={form.horizontalAlignment}
        isBold={form.isBold}
        setInForm={setInForm}
        validateField={validateField}
      />
    </DynamicFieldOptionsLayout>
  );
};

DynamicFieldHandWritingSignatureOptions.propTypes = {
  setParentView: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  chosenOption: PropTypes.array.isRequired,
  editor: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  action: PropTypes.object,
  style: PropTypes.object,
  isNewItem: PropTypes.bool,
};

export default DynamicFieldHandWritingSignatureOptions;
